<template>
  <riseup-header
    :flow-config-id="FlowConfigIdType.Syndesi"
    :is-green="true"
    :white-label-firm-id="whiteLabelFirmId"
    :force-background-color="'#292929'"
  />
  <div
    v-if="showContentLoader"
    class="loader-wrapper"
  >
    <tiny-loader
      class="loader-class"
      variant="secondary-black"
    />
  </div>
  <div
    v-else
    class="signup"
  >
    <Descope
      :client="{
        anonymousUserId: anonymousSession.getId(),
        flowConfigId,
        promptId,
        utmParams,
        contextPageReferrer,
        whiteLabelFirmId: whiteLabelFirmId,
      }"
      :error-transformer="errorTransformer"
      :flow-id="descopeFlow"
      style-id="syndesi"
      @error="handleDescopeError"
      @success="handleSuccess"
    />
  </div>
</template>

<script lang="ts" setup>
import { Descope, useSession } from '@descope/vue-sdk';
import TinyLoader from '@riseupil/base-ui/src/components/TinyLoader/TinyLoader.vue';
import Cookies from 'js-cookie';
import _ from 'lodash';
import { computed, onBeforeMount, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import DDLogs from '../../DDLogs';
import Segment from '../../Segment';
import anonymousSession from '../../anonymousSession';
import useContentLoader from '../../composables/useContentLoader';
import { useOtsJob } from '../../services/otsConfigService';
import { FlowConfigIdType, WhiteLabelFirmIdType } from '../../types/otsConfig';
import { errorTransformer, handleDescopeError } from '../../utils/errorHandling';
import RiseupHeader from '../Common/RiseupHeader.vue';
import { completeDescopeOtsSignup } from '../../api/loginApi';

const { setOtsJobConfig } = useOtsJob();
const router = useRouter();
const { toggleLoader, showContentLoader } = useContentLoader();
toggleLoader(true);
watch(useSession().isLoading, async newVal => {
  toggleLoader(newVal);
});

const { sessionToken } = useSession();
const utmParams = _.pickBy(router.currentRoute.value.query, (value, key) => key.startsWith('utm_') && value);
const flowConfigId = FlowConfigIdType.Syndesi;
const promptId = router.currentRoute.value.query.pid || router.currentRoute.value.query.utm_pvr;
const contextPageReferrer = document.referrer;

const route = useRoute();

const whiteLabelFirmId = computed(() => (
  (route.meta.whiteLabelFirmId as WhiteLabelFirmIdType)
    || WhiteLabelFirmIdType.DNS
));

onBeforeMount(async () => {
  const queryParam = route.query || null;
  if (!queryParam['descope-login-flow']) {
    await Segment.reset();
    Segment.identifyAnonymousUser();
  }
});

const handleSuccess = async (e: CustomEvent) => {
  toggleLoader(true);
  DDLogs.info('DescopeSyndesiOtsSignup handleSuccess');
  const token = sessionToken.value ?? e.detail.sessionJwt;

  if (!token) {
    throw new Error('Descope Signup flow failed to signup, user sessionJwt is empty');
  }
  const { product, syndesiId } = router.currentRoute.value.query;

  const gclid = Cookies.get('gclid');

  const resp = await completeDescopeOtsSignup(
    token,
    utmParams,
    { flowConfigId, promptId },
    gclid,
    syndesiId,
  );

  Segment.identifyCustomer(resp.customerId, resp.memberId);

  if (!e.detail.user.customAttributes.customerId) {
    Segment.trackUserGot('CustomerCreated_Marketing', {
      market: 'UK',
      product,
      gclid,
      customerId: resp.customerId,
      utmParams,
      otsParams: {
        flowConfigId,
        promptId,
        whiteLabelFirmId: whiteLabelFirmId.value,
      },
      syndesiId,
    });
  }
  const { jobId } = resp;
  setOtsJobConfig(flowConfigId, jobId);

  if (router.currentRoute.value.query.utm_variant || router.currentRoute.value.query.utm_pvr) {
    DDLogs.warn('OTS used with old utm_params', {
      customer_id: resp.customerId,
      utm_pvr: promptId,
      utm_var: flowConfigId,
    });
  }

  if (whiteLabelFirmId.value === WhiteLabelFirmIdType.DefaultFirm) {
    router.push('/ots/syndesi-wl/connect');
  } else {
    router.push('/ots/syndesi/connect');
  }
};

const descopeFlow = computed(() => 'ots-syndesi-signup');

const onMetaBrowser = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return (userAgent.indexOf('FBAN') > -1 || userAgent.indexOf('FBAV') > -1 || userAgent.indexOf('Instagram') > -1);
};

const currentHash = window.location.hash;

if (onMetaBrowser() && !currentHash) {
  window.location.hash = '#FBK';
}
</script>

<style lang="scss" scoped>
@import '../../scss/riseup-vars';

.signup {
  background-color: white;
  overflow-y: auto;
  scroll-behavior: smooth;
}

.loader-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}
</style>
